import Chip from '@material-ui/core/Chip'
import IconButton from '@material-ui/core/IconButton'
import { makeStyles } from '@material-ui/core/styles'
import TableCell from '@material-ui/core/TableCell'
import TableRow from '@material-ui/core/TableRow'
import Tooltip from '@material-ui/core/Tooltip'
import CheckCircleOutlinedIcon from '@material-ui/icons/CheckCircleOutlined'
import LabelIcon from '@material-ui/icons/Label'
import RadioButtonUncheckedOutlinedIcon from '@material-ui/icons/RadioButtonUncheckedOutlined'
import VisibilityIcon from '@material-ui/icons/Visibility'
import clsx from 'clsx'
import dayjs from 'dayjs'
import React, { useEffect, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { useDispatch, useSelector } from 'react-redux'
import { dailyUpdatePaymentStatusFromPurchases } from 'src/redux/slices/purchases'
import { separator } from '../../../utils/numberSeparator'

const useStyles = makeStyles((theme) => ({
	root: {
		width: '90%',
		margin: 'auto',
		boxShadow: '0px 2px 4px 0px #0000003D',
	},
	paper: {
		width: '100%',
		marginBottom: theme.spacing(2),
	},
	table: {
		minWidth: 750,
	},
	visuallyHidden: {
		border: 0,
		clip: 'rect(0 0 0 0)',
		height: 1,
		margin: -1,
		overflow: 'hidden',
		padding: 0,
		position: 'absolute',
		top: 20,
		width: 1,
	},
	tableBody: {
		margin: '1rem',
		background: 'white',
		borderRadius: '10px',
		width: 'unset',
		border: '1px solid #C2C9D1',
	},
	downloadLink: {
		color: 'blue',
		textDecoration: 'underline',
		'& p': {
			margin: 0,
			width: 'fit-content',
			'&:hover': {
				cursor: 'pointer',
			},
		},
	},
	status: {
		'& p': {
			// margin: 0,
			width: '4rem',
			border: '1px solid black',
			padding: '0 0.2rem',
			borderRadius: '4px',
			textAlign: 'center',
			whiteSpace: 'nowrap',
			margin: '0 auto',
			cursor: 'default',
		},
	},
	statusDraft: {
		'& p': {
			borderColor: '#3CC13B',
			color: '#3CC13B',
			padding: '0 0.2rem',
			width: '4rem',
			textAlign: 'center',
			whiteSpace: 'nowrap',
			margin: '0 auto',
		},
	},
	statusPaid: {
		'& p': {
			borderColor: '#336B87',
			color: '#336B87',
			padding: '0 0.2rem',
			width: '4rem',
			textAlign: 'center',
			whiteSpace: 'nowrap',
			margin: '0 auto',
		},
	},
	statusPending: {
		'& p': {
			borderColor: '#F3BB1C',
			color: '#F3BB1C',
			padding: '0 0.2rem',
			width: '4rem',
			textAlign: 'center',
			whiteSpace: 'nowrap',
			margin: '0 auto',
		},
	},
	statusToPay: {
		'& p': {
			borderColor: '#9469A9',
			color: '#9469A9',
			padding: '0 0.2rem',
			width: '4rem',
			textAlign: 'center',
			whiteSpace: 'nowrap',
			margin: '0 auto',
		},
	},
	statusOverdue: {
		'& p': {
			borderColor: '#F03738',
			color: '#F03738',
			padding: '0 0.2rem',
			width: '4rem',
			textAlign: 'center',
			whiteSpace: 'nowrap',
			margin: '0 auto',
		},
	},
	statusPaidLate: {
		'& p': {
			borderColor: '#006600',
			color: '#006600',
			padding: '0 0.2rem',
			width: '4rem',
			textAlign: 'left',
		},
	},
	cellIcons: {
		display: 'flex',
		flexDirection: 'row',
		justifyContent: 'space-evenly',
		width: '6rem',
		'& svg': {
			width: '0.9em',
			height: '0.9em',
		},
	},
	isArchived: {
		backgroundColor: 'rgba(211,211,211, 0.5)',
		color: 'rgba(211,211,211, 0.5)',
		'& td,th': {
			color: 'gray',
		},
		'& th': {
			color: 'gray',
			backgroundColor: 'transparent',
		},
		'& p': {
			// margin: 0,
			margin: '0 auto',
			width: 'fit-content',
		},
	},
	emptyText: {
		height: 'inherit',
		display: 'flex',
		flexDirection: 'column',
		alignItems: 'center',
		justifyContent: 'center',
		'& p': {
			marginBottom: '0rem',
		},
	},
	loadingIcon: {
		height: 'inherit',
		display: 'flex',
		flexDirection: 'column',
		justifyContent: 'center',
		'& p': {
			marginBottom: '0rem',
		},
	},
	ellipsis: {
		maxWidth: '0',
		whiteSpace: 'nowrap',
		textOverflow: 'ellipsis',
	},
	tags: {
		maxWidth: '5rem',
		marginRight: '0.2rem',
		marginBottom: '0.2rem',
	},
	tableRow: {
		'& > td': {
			padding: '16px',
		},
	},
}))

function SupplierTransactionTable({
	labelId,
	row,
	companyInfo,
	addTags,
	viewInvoices,
}) {
	const classes = useStyles()
	const { t, i18n } = useTranslation()
	const { t: tPurchases } = useTranslation('purchases')
	const { t: tSupplierTransaction } = useTranslation('supplierTransaction')
	const dispatch = useDispatch()
	const [currentLanguage, setCurrentLanguage] = useState(null)
	// biome-ignore lint: Payment status check - updates overdue payment status on mount
	useEffect(() => {
		if (
			row?.purchaseInvoiceDueDate &&
			dayjs(row?.purchaseInvoiceDueDate).diff(dayjs(), 'day') < 0 &&
			row?.paymentStatus === 'Pending'
		) {
			dispatch(
				dailyUpdatePaymentStatusFromPurchases({ currentPurchase: row })
			)
		}
	}, [])

	useEffect(() => {
		setCurrentLanguage(i18n.language)
	}, [i18n.language])

	return (
		<TableRow
			role="checkbox"
			tabIndex={-1}
			key={row.id}
			className={clsx({
				[classes.isArchived]: row.paymentStatus === 'Archived',
				[classes.tableRow]: true,
			})}
			hover={row.paymentStatus !== 'Archived'}
		>
			<TableCell
				style={{ overflow: 'hidden', paddingLeft: '1rem' }}
				component="th"
				id={labelId}
				scope="row"
				padding="default"
				className={clsx(classes.ellipsis, {
					[classes.isArchived]: row.paymentStatus === 'Archived',
					// [classes.downloadLink]: row.paymentStatus !== 'Archived',
				})}
			>
				{dayjs(row.createdOn).format('DD/MM/YYYY')}
			</TableCell>
			<TableCell
				className={classes.ellipsis}
				style={{ overflow: 'hidden' }}
				align="left"
			>
				{row.supplierName}
			</TableCell>
			<TableCell
				className={classes.ellipsis}
				style={{ overflow: 'hidden' }}
				align="left"
			>
				{currentLanguage === 'en'
					? row.recapCostlineItem
					: row.recapCostlineItem_fr}
			</TableCell>
			<TableCell
				className={classes.ellipsis}
				style={{ overflow: 'hidden' }}
				align="left"
			>
				{row.quoteName}
			</TableCell>
			<TableCell
				className={classes.ellipsis}
				style={{ overflow: 'hidden' }}
				align="left"
			>
				{row.projectName}
			</TableCell>
			{/* <Tooltip
        title={row.supplierCreditorAccount ? row.supplierCreditorAccount : ''}
        aria-label={'Supplier Creditor Account'}
      > */}
			<TableCell
				className={classes.ellipsis}
				align="left"
				style={{ overflow: 'hidden', textTransform: 'capitalize' }}
			>
				{row.supplierType ? row.supplierType : '-'}
			</TableCell>
			{/* </Tooltip> */}
			<TableCell
				className={classes.ellipsis}
				align="right"
				style={{
					// paddingRight: '3.5rem',
					overflow: 'hidden',
				}}
			>
				{separator(
					(Math.round(row.provisionalTotal * 100) / 100).toFixed(2)
				)}
			</TableCell>
			<TableCell
				className={classes.ellipsis}
				align="right"
				style={{
					// paddingRight: '3.5rem',
					backgroundColor: row.groupType && 'rgba(0, 0, 0, 0.04)',
					borderBottom: row.groupType && '0',
					overflow: 'hidden',
				}}
			>
				{row.groupType !== 'secondary' &&
					separator(
						(Math.round(row.invoiceTotal * 100) / 100).toFixed(2)
					)}
			</TableCell>
			<TableCell
				className={classes.ellipsis}
				align="right"
				style={{
					// paddingRight: '3.5rem',
					backgroundColor: row.groupType && 'rgba(0, 0, 0, 0.04)',
					borderBottom: row.groupType && '0',
					overflow: 'hidden',
				}}
			>
				{row.groupType !== 'secondary' &&
					separator((row.invoiceTotal + row.taxAmount).toFixed(2))}
			</TableCell>

			<TableCell
				//  className={classes.ellipsis} style={{ overflow: 'hidden' }}
				align="left"
			>
				{row.poAvailable ? (
					<CheckCircleOutlinedIcon
						fontSize="small"
						style={{
							color: '#3CC13B',
							display: 'flex',
							margin: 'auto',
						}}
					/>
				) : (
					<RadioButtonUncheckedOutlinedIcon
						fontSize="small"
						style={{ display: 'flex', margin: 'auto' }}
					/>
				)}
			</TableCell>
			{/* <TableCell
        //  className={classes.ellipsis} style={{ overflow: 'hidden' }}
        align='left'
      >
        {row.invoiceAvailable ? (
          <CheckCircleOutlinedIcon
            fontSize='small'
            style={{ color: '#3CC13B', display: 'flex', margin: 'auto' }}
          />
        ) : (
          <RadioButtonUncheckedOutlinedIcon
            fontSize='small'
            style={{ display: 'flex', margin: 'auto' }}
          />
        )}
      </TableCell> */}
			{/* <TableCell className={classes.ellipsis} style={{ overflow: 'hidden' }} align='left'>
        {row.paidDate && row.paymentStatus === 'Paid'
          ? dayjs(row.paidDate).format('DD/MM/YYYY')
          : '-'}
      </TableCell> */}
			<TableCell
				// style={{ display: 'flex' }}
				align="left"
			>
				{row.tags
					? row.tags.map((t) => {
							const color = companyInfo?.purchaseTags?.filter(
								(pt) => pt.title === t
							)[0].color
							return (
								<Chip
									label={t}
									// onDelete={handleDelete}
									// color='primary'
									style={{ backgroundColor: color }}
									size="small"
									className={classes.tags}
								/>
							)
						})
					: ''}
			</TableCell>
			<Tooltip
				title={
					row.paymentStatus === 'Paid'
						? dayjs(row.purchasePaidDate).format('DD/MM/YYYY')
						: ''
				}
				aria-label={'Paid Date Tooltip'}
				arrow
			>
				<TableCell
					// style={{ overflow: 'hidden' }}
					align="left"
					className={clsx(
						classes.status,
						// classes.ellipsis,
						{
							[classes.statusDraft]:
								row.paymentStatus === 'Draft',
							[classes.statusPaid]: row.paymentStatus === 'Paid',
							[classes.statusPending]:
								row.paymentStatus === 'Pending',
							[classes.statusOverdue]:
								row.paymentStatus === 'Overdue',
							[classes.statusToPay]:
								row.paymentStatus === 'To Pay',
						}
					)}
				>
					<p>{tPurchases(`status.${row.paymentStatus}`)}</p>
				</TableCell>
			</Tooltip>
			<TableCell
				// className={classes.ellipsis}
				style={{ overflow: 'hidden', paddingRight: '1.5rem' }}
				align="center"
			>
				<div className={classes.cellIcons}>
					<Tooltip
						title={tSupplierTransaction('viewInvoice')}
						aria-label={'View'}
					>
						<IconButton
							size="small"
							color="primary"
							onClick={() => viewInvoices(row, row.supplierName)}
							disabled={
								row.paymentStatus === 'Archived' ||
								!row.invoiceAvailable
							}
						>
							<VisibilityIcon />
						</IconButton>
					</Tooltip>
					{/* <Tooltip title={tSupplierTransaction('addTags')} aria-label={'Add Tags'}>
            <IconButton
              size='small'
              color='primary'
              onClick={() => addTags(row)}
              disabled={row.paymentStatus === 'Archived'}
            >
              <LabelIcon />
            </IconButton>
          </Tooltip> */}
				</div>
			</TableCell>
		</TableRow>
	)
}

export default SupplierTransactionTable
